"use client";

import React, { useState, memo, useEffect, useRef } from "react";
import { Box, Container, Typography, Button, Stack, Chip } from "@mui/material";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import "../app/globals.css";
import { IMAGES } from "../constants/images";
import NextLink from "next/link";
const ThreeEasySteps = memo(() => {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const [selectedStep, setSelectedStep] = useState(0);
  const containerRef = useRef(null);
  const stepsRef = useRef([]);

  const steps = [
    {
      number: "1",
      title: t("threeSteps.steps.step1.title"),
      description: t("threeSteps.steps.step1.description"),
      image: IMAGES.STEP_1_IMAGE,
      imageAlt:
        "Step 1: Sign up for the best AI recruiting tool and recruitment automation software",
      color: "#4ade80",
      bgColor: "rgba(74, 222, 128, 0.1)",
      borderColor: "rgba(74, 222, 128, 0.3)",
    },
    {
      number: "2",
      title: t("threeSteps.steps.step2.title"),
      description: t("threeSteps.steps.step2.description"),
      image: IMAGES.STEP_2_IMAGE,
      imageAlt:
        "Step 2: Activate automated recruiting software and top recruiting platform features",
      color: "#22d3ee",
      bgColor: "rgba(34, 211, 238, 0.1)",
      borderColor: "rgba(34, 211, 238, 0.3)",
    },
    {
      number: "3",
      title: t("threeSteps.steps.step3.title"),
      description: t("threeSteps.steps.step3.description"),
      image: IMAGES.STEP_3_IMAGE,
      imageAlt:
        "Step 3: Review and hire candidates with the best recruiting automation software",
      color: "#a855f7",
      bgColor: "rgba(168, 85, 247, 0.1)",
      borderColor: "rgba(168, 85, 247, 0.3)",
    },
  ];

  // Scroll-based step activation
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;

      stepsRef.current.forEach((stepElement, index) => {
        if (!stepElement) return;

        const stepRect = stepElement.getBoundingClientRect();
        const stepCenter = stepRect.top + stepRect.height / 2;
        const distanceFromCenter = Math.abs(stepCenter - viewportCenter);

        // Activate step closest to viewport center
        if (distanceFromCenter < stepRect.height) {
          setSelectedStep(index);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClick = () => {
    router.push("/free-trial-of-ai-recruiting-tool");
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 4, sm: 5, md: 8 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 3, md: 3 },
        }}
      >
        {/* Eyebrow Text */}
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", md: "center" },
          }}
        >
          <Typography
            sx={{
              color: "#5B6B77",
              fontFamily: "var(--font-outfit)",
              fontSize: {
                xs: "0.85rem",
                sm: "1rem",
                md: "1rem",
                lg: "1rem",
                xl: "1.3rem",
              },
              fontWeight: 400,
              fontStyle: "italic",
              mt: { sm: 1, md: "-3%" },
            }}
          >
            {t("threeSteps.eyebrow")}
          </Typography>
        </Box>

        {/* Chip */}
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", md: "center" },
          }}
        >
          <Chip
            label={t("threeSteps.chip")}
            sx={{
              backgroundColor: "transparent",
              border: "1px solid #DDE4E8",
              mb: { xs: 2, sm: 2.5, md: 1 },
              color: "#647482",
              fontSize: { xs: "12px", sm: "14px", md: "16px" },
              fontWeight: "400",
              textTransform: "capitalize",
              px: { xs: 0.5, sm: 0.8 },
              py: { xs: 1.2, sm: 1.5, md: 2.4 },
              fontFamily: "var(--font-outfit)",
              borderRadius: "40px",
              textAlign: "center",
              ml: { sm: 1, md: 0, lg: 0 },
              mt: { xs: "9%", sm: "9%", md: "5%", lg: "4%" },
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontSize: {
              xs: "1.5rem",
              sm: "1.8rem",
              md: "2rem",
              lg: "2rem",
              xl: "2.75rem",
            },
            fontWeight: 600,
            mb: { xs: 2, md: 3 },
            fontFamily: "var(--font-plusjakarta)",
            textAlign: { xs: isArabic ? "right" : "left", md: "center" },
            mt: { xs: 1, md: 0, lg: "3%", xl: "2%" },
            lineHeight: "1.3",
          }}
        >
          {t("threeSteps.title")}
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            fontSize: {
              xs: "0.9rem",
              sm: "0.95rem",
              md: "1rem",
              lg: "1rem",
              xl: "1.2rem",
            },
            color: "#02061799",
            fontFamily: "var(--font-outfit)",
            textAlign: { xs: isArabic ? "right" : "left", md: "center" },
            mb: { xs: 4, md: 6 },
            fontWeight: 400,
            maxWidth: { xs: "100%", md: "80%" },
            mx: { xs: 0, md: "auto" },
          }}
        >
          {t("threeSteps.subtitle")}
        </Typography>

        {/* Layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            gap: { xs: 4, md: 10 },
          }}
        >
          {/* Step List */}
          <Box sx={{ flex: 1, maxWidth: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {steps.map((step, index) => {
                const isSelected = selectedStep === index;

                return (
                  <motion.div
                    key={index}
                    ref={(el) => (stepsRef.current[index] = el)}
                    initial={{ opacity: 0, x: isArabic ? 50 : -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2.5,
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => setSelectedStep(index)}
                    >
                      {/* Number Circle */}
                      <Box
                        component={motion.div}
                        animate={{
                          scale: isSelected ? 1.15 : 1,
                        }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        sx={{
                          bgcolor: isSelected ? step.color : "transparent",
                          color: isSelected ? "#fff" : "#9048FB",
                          width: { xs: 24, md: 28 },
                          height: { xs: 24, md: 28 },
                          borderRadius: "50%",
                          border: isSelected ? "none" : "2px solid #9048FB",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: { xs: "0.8rem", md: "0.9rem" },
                          fontWeight: 600,
                          flexShrink: 0,
                          zIndex: 1,
                          transition: "all 0.3s ease",
                          mt: { xs: "14px", md: "18px" },
                        }}
                      >
                        {step.number}
                      </Box>

                      {/* Text Box */}
                      <Box
                        sx={{
                          flex: 1,
                          p: { xs: 1.5, md: 2 },
                          borderRadius: "12px",
                          backgroundColor: isSelected
                            ? step.bgColor
                            : "transparent",
                          border: isSelected
                            ? `2px solid ${step.borderColor}`
                            : "2px solid transparent",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? step.bgColor
                              : "rgba(0,0,0,0.03)",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: {
                              xs: "1.1rem",
                              sm: "1.2rem",
                              md: "1.3rem",
                            },
                            color: isSelected ? step.color : "#1C2843",
                            transition: "color 0.3s ease",
                            fontFamily: "var(--font-outfit)",
                          }}
                        >
                          {step.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#666",
                            lineHeight: 1.6,
                            fontSize: {
                              xs: "0.9rem",
                              sm: "0.95rem",
                              md: "1rem",
                            },
                            fontFamily: "var(--font-outfit)",
                          }}
                        >
                          {step.description}
                        </Typography>
                      </Box>

                      {/* Vertical Line */}
                      {index !== steps.length - 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: isArabic ? "auto" : { xs: 11, md: 13 },
                            right: isArabic ? { xs: 11, md: 13 } : "auto",
                            top: { xs: 30, md: 52 },
                            width: "2px",
                            height: {
                              xs: "calc(100% - 24px)",
                              md: "calc(100% - 36px)",
                            },
                            bgcolor: "#C6BAB3",
                            zIndex: 0,
                          }}
                        />
                      )}
                    </Box>
                  </motion.div>
                );
              })}
            </Box>

            {/* CTA Button */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{
                mb: 4,
                mt: "7%",
                ml: { xs: 0, md: isArabic ? 0 : "11%" },
                mr: { xs: 0, md: isArabic ? "11%" : 0 },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <NextLink href="/free-trial-of-ai-recruiting-tool" passHref legacyBehavior>
                <Button
                  component="a"
                  sx={{
                    bgcolor: "#775CE5",
                    color: "#fff",
                    textTransform: "none",
                    px: 3,
                    py: 1.7,
                    height: { xs: "50px", md: "56px" },
                    width: { xs: "100%", sm: "auto" },
                    fontFamily: "var(--font-outfit)",
                    fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1rem" },
                    fontWeight: "400",
                    borderRadius: "28px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      bgcolor: "#000",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  {t("threeSteps.cta")}
                </Button>
              </NextLink>
            </Stack>
          </Box>

          {/* Image Area with Sticky Scroll */}
          <Box
            sx={{
              flex: 1,
              position: { xs: "relative", md: "sticky" },
              top: { md: "100px" },
              height: { xs: "250px", sm: "300px", md: "460px" },
              width: "100%",
              maxWidth: { xs: "100%", md: "50%" },
              display: { xs: "none", md: "block" },
            }}
          >
            <motion.div
              key={selectedStep}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              style={{ width: "100%", height: "100%", position: "relative" }}
            >
              <Image
                src={steps[selectedStep].image}
                alt={`${steps[selectedStep].imageAlt} preview`}
                fill
                style={{
                  objectFit: "contain",
                  objectPosition: "center",
                }}
                priority
              />
            </motion.div>
          </Box>
        </Box>
      </Container>
    </Box>
  );
});

ThreeEasySteps.displayName = "ThreeEasySteps";

export default ThreeEasySteps;
